/* @flow */

import { values } from 'lodash'
import { put, takeEvery } from 'redux-saga/effects'
import { getAllCustomers } from '../fetch/customers'
import type {
  RootState,
  Action,
  Customer,
  CustomerId,
  CustomersStore,
  User,
  PollCopyFilters,
} from '../types'
import { insertOrMerge, ActionRequiresPropertyError } from '../helpers'

// ACTIONS
export const FETCH_CUSTOMERS: string = 'opinary/FETCH_CUSTOMERS'
export const INTO_CUSTOMERS_STORE: string = 'opinary/INTO_CUSTOMERS_STORE'
export const CHANGE_FILTER: string = 'CHANGE_FILTER'

export const FETCH_CUSTOMERS_VERTICALS: string =
  'opinary/FETCH_CUSTOMERS_VERTICALS'
export const FETCH_CUSTOMERS_VERTICALS_SUCCESS: string =
  'opinary/FETCH_CUSTOMERS_VERTICALS_SUCCESS'
export const FETCH_CUSTOMERS_VERTICALS_ERROR: string =
  'opinary/FETCH_CUSTOMERS_VERTICALS_ERROR'

export function fetchCustomers(): Action {
  return {
    type: FETCH_CUSTOMERS,
  }
}

export const fetchCustomerVerticals = (id: number): Action => ({
  type: FETCH_CUSTOMERS_VERTICALS,
  id,
})

export const fetchCustomerVerticalsSuccess = (verticals: any): Action => ({
  type: FETCH_CUSTOMERS_VERTICALS_SUCCESS,
  verticals,
})

export const fetchCustomerVerticalsFail = (): Action => ({
  type: FETCH_CUSTOMERS_VERTICALS_ERROR,
})

export function intoCustomersStore(items: Array<Customer>): Action {
  return {
    type: INTO_CUSTOMERS_STORE,
    items,
  }
}

export function selectFilteredCustomers(rootState: RootState): Array<Object> {
  const filters = rootState.customersStore.filter
  const customers = values(rootState.customersStore)

  const filteredCustomers = customers
    .filter(customer => {
      if (filters.country === 'all') return true
      const selectedRegions = filters.country.split(',')
      return selectedRegions.includes(customer.country)
    })
    .filter(customer => {
      if (filters.brand === 'all') return true
      return customer.brand === filters.brand
    })
    .filter(customer => {
      switch (filters.status) {
        case 'automated':
          return !!customer.automation && !customer.hasNicheAutomation
        case 'bams':
          return !!customer.hasNicheAutomation
        case 'library':
          return !!customer.displayLibrary
        case 'others':
          return !customer.displayLibrary
        default:
          return true
      }
    })

  return filteredCustomers
}

export function selectCustomers(rootState: RootState): Array<Customer> {
  return values(rootState.customersStore)
}

export function filterCustomers(filter: PollCopyFilters): Action {
  return {
    type: CHANGE_FILTER,
    filter,
  }
}

export function selectCustomerById(
  rootState: RootState,
  id?: CustomerId | null
): ?Customer {
  if (!id) return null
  return rootState.customersStore[id]
}

export const selectCustomerVerticals = (rootState: RootState) =>
  rootState.customersStore.customerVerticals

// MAPPING
export function mapUserToCustomerStore(user: User) {
  return [
    {
      id: user.customerId,
      name: user.customerId,
    },
  ]
}

// REDUCER
export default function reducer(
  state: CustomersStore = {
    filter: {
      country: 'all',
      brand: 'all',
      status: 'all',
    },
    customerVerticals: [],
  },
  action: Object
): CustomersStore {
  switch (action.type) {
    case INTO_CUSTOMERS_STORE:
      if (!action.items) {
        throw new ActionRequiresPropertyError(INTO_CUSTOMERS_STORE, 'items')
      } else {
        return insertOrMerge(state, action.items)
      }

    case FETCH_CUSTOMERS_VERTICALS_SUCCESS:
      return { ...state, customerVerticals: action.verticals }
    case FETCH_CUSTOMERS_VERTICALS_ERROR:
      return { ...state, customerVerticals: [] }

    case CHANGE_FILTER:
      return {
        ...state,
        filter: action.filter,
      }

    default:
      return state
  }
}

export function* fetchCustomersSaga(): Generator<*, void, *> {
  yield takeEvery(FETCH_CUSTOMERS, function*() {
    const customers = yield getAllCustomers()
    if (!customers) return
    yield put(intoCustomersStore(customers))
  })
}



// WEBPACK FOOTER //
// ./src/stores/customersStore.js